import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LegalFooter from "../components/LegalFooter";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithGoogle, requestPasswordReset } = useAuth();

  // "login" | "register" | "forgot"
  const [mode, setMode] = useState(() => searchParams.get("mode") || "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  // Structured error to render inline banners with helpful CTAs.
  // { kind: "no_account"|"bad_password"|"email_taken"|"generic", message }
  const [inlineError, setInlineError] = useState(null);

  const validation = useMemo(() => {
    const errors = {};
    if (email && !EMAIL_RE.test(email)) errors.email = "Adresse email invalide.";
    if (mode === "register") {
      if (password && password.length < 8)
        errors.password = "Le mot de passe doit contenir au moins 8 caractères.";
      if (confirm && confirm !== password)
        errors.confirm = "Les mots de passe ne correspondent pas.";
    }
    return errors;
  }, [email, password, confirm, mode]);

  const switchMode = (m) => {
    setMode(m);
    setInlineError(null);
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setInlineError(null);
    if (Object.keys(validation).length) return;
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Connecté");
      navigate(u?.role === "admin" ? "/admin" : "/");
    } catch (err) {
      const msg = err.message || "Erreur inconnue";
      if (/aucun compte n'existe/i.test(msg)) {
        setInlineError({ kind: "no_account", message: msg });
      } else if (/mot de passe incorrect/i.test(msg)) {
        setInlineError({ kind: "bad_password", message: msg });
      } else {
        setInlineError({ kind: "generic", message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setInlineError(null);
    if (Object.keys(validation).length) return;
    if (password !== confirm) {
      setInlineError({ kind: "generic", message: "Les mots de passe ne correspondent pas." });
      return;
    }
    setLoading(true);
    try {
      await register(email, password);
      toast.success("Compte créé");
      // RequireNickname guard will bounce to /profile?need=nickname automatically
      navigate("/");
    } catch (err) {
      const msg = err.message || "Erreur inconnue";
      if (/existe déjà/i.test(msg)) {
        setInlineError({ kind: "email_taken", message: msg });
      } else {
        setInlineError({ kind: "generic", message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setInlineError(null);
    if (!EMAIL_RE.test(email)) {
      setInlineError({ kind: "generic", message: "Adresse email invalide." });
      return;
    }
    setLoading(true);
    try {
      const res = await requestPasswordReset(email);
      toast.success(
        res?.message ||
          "La réinitialisation du mot de passe sera bientôt disponible. En attendant, contactez le support OTRAQUIZZ.",
      );
      switchMode("login");
    } catch (err) {
      setInlineError({ kind: "generic", message: err?.message || "Erreur inconnue" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative grid-bg flex flex-col px-6">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-fuchsia-500/10 pointer-events-none" />
      <div className="relative flex-1 flex items-center justify-center py-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="flex items-center gap-2 justify-center mb-8"
            data-testid="login-logo"
          >
            <Zap className="w-6 h-6 text-cyan-400" />
            <span className="font-display font-black uppercase tracking-tighter text-xl">
              Otra<span className="text-cyan-400 neon-text-primary">Quizz</span>
            </span>
          </Link>

          <div className="panel rounded-3xl p-6 sm:p-8 space-y-5" data-testid={`auth-panel-${mode}`}>
            <Header mode={mode} />

            {/* Google button is always available except in forgot flow */}
            {mode !== "forgot" && (
              <>
                <button
                  data-testid="login-google-button"
                  type="button"
                  onClick={loginWithGoogle}
                  className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl bg-white text-black font-display font-bold uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
                >
                  <GoogleGlyph />
                  Continuer avec Google
                </button>
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/30">
                  <div className="h-px flex-1 bg-white/10" />
                  ou
                  <div className="h-px flex-1 bg-white/10" />
                </div>
              </>
            )}

            {inlineError && (
              <InlineError
                error={inlineError}
                onSwitchToRegister={() => {
                  switchMode("register");
                  setInlineError(null);
                }}
                onSwitchToLogin={() => {
                  switchMode("login");
                  setInlineError(null);
                }}
                onSwitchToForgot={() => switchMode("forgot")}
              />
            )}

            {mode === "login" && (
              <form onSubmit={onLogin} className="space-y-4" data-testid="login-form">
                <EmailField value={email} onChange={setEmail} error={validation.email} testid="login-email-input" />
                <PasswordField
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  testid="login-password-input"
                  toggleTestid="login-password-toggle"
                />
                <button
                  data-testid="login-submit-button"
                  disabled={loading || !!validation.email}
                  className="w-full px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 transition-all glow-primary"
                >
                  {loading ? "Connexion..." : "Se connecter"}
                </button>
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="text-white/60 hover:text-cyan-300"
                    data-testid="login-forgot-link"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="pt-2 border-t border-white/10 text-center text-sm text-white/60">
                  Pas encore de compte ?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("register")}
                    className="text-cyan-300 hover:text-cyan-200 font-bold"
                    data-testid="auth-switch-to-register"
                  >
                    Créer un compte
                  </button>
                </div>
              </form>
            )}

            {mode === "register" && (
              <form onSubmit={onRegister} className="space-y-4" data-testid="register-form">
                <EmailField value={email} onChange={setEmail} error={validation.email} testid="register-email-input" />
                <PasswordField
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  testid="register-password-input"
                  toggleTestid="register-password-toggle"
                  error={validation.password}
                  label="Mot de passe"
                  placeholder="Min. 8 caractères"
                />
                <PasswordField
                  value={confirm}
                  onChange={setConfirm}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((v) => !v)}
                  testid="register-confirm-input"
                  toggleTestid="register-confirm-toggle"
                  error={validation.confirm}
                  label="Confirmer le mot de passe"
                  placeholder="Retape le mot de passe"
                />
                <button
                  data-testid="register-submit-button"
                  disabled={
                    loading ||
                    !!validation.email ||
                    !!validation.password ||
                    !!validation.confirm ||
                    !password ||
                    !confirm
                  }
                  className="w-full px-6 py-4 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-wider active:scale-95 disabled:opacity-50 transition-all glow-secondary"
                >
                  {loading ? "Création..." : "Créer mon compte"}
                </button>
                <div className="pt-2 border-t border-white/10 text-center text-sm text-white/60">
                  Déjà un compte ?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="text-cyan-300 hover:text-cyan-200 font-bold"
                    data-testid="auth-switch-to-login"
                  >
                    Se connecter
                  </button>
                </div>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={onForgot} className="space-y-4" data-testid="forgot-form">
                <p className="text-sm text-white/60">
                  Renseigne ton email, on te guidera pour réinitialiser ton mot de passe.
                </p>
                <EmailField value={email} onChange={setEmail} error={validation.email} testid="forgot-email-input" />
                <button
                  data-testid="forgot-submit-button"
                  disabled={loading || !EMAIL_RE.test(email)}
                  className="w-full px-6 py-4 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 transition-all"
                >
                  {loading ? "Envoi..." : "Envoyer les instructions"}
                </button>
                <div className="text-center text-sm text-white/60">
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="text-cyan-300 hover:text-cyan-200"
                    data-testid="forgot-back-to-login"
                  >
                    ← Retour à la connexion
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-white/40 text-xs mt-6">
            En te connectant, tu acceptes que ton email vérifié soit associé à tes participations.
          </p>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

function Header({ mode }) {
  const titles = {
    login: { h1: "Connexion", sub: "Retrouve tes codes, tes scores et tes gains." },
    register: {
      h1: "Créer un compte",
      sub: "Rejoins la communauté OTRAQUIZZ en quelques secondes.",
    },
    forgot: {
      h1: "Mot de passe oublié",
      sub: "On t'aide à récupérer l'accès à ton compte.",
    },
  };
  const t = titles[mode] || titles.login;
  return (
    <div>
      <h1 className="font-display text-3xl font-bold" data-testid={`auth-title-${mode}`}>
        {t.h1}
      </h1>
      <p className="text-white/60 text-sm mt-1">{t.sub}</p>
    </div>
  );
}

function InlineError({ error, onSwitchToRegister, onSwitchToLogin, onSwitchToForgot }) {
  return (
    <div
      data-testid={`auth-inline-error-${error.kind}`}
      className="rounded-xl border border-rose-400/40 bg-rose-400/10 p-3 text-sm text-rose-100 space-y-2"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error.message}</span>
      </div>
      {error.kind === "no_account" && (
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="w-full px-3 py-2 rounded-lg bg-rose-400 text-black text-xs uppercase tracking-widest font-mono-stat font-bold hover:bg-rose-300"
          data-testid="auth-error-create-btn"
        >
          Créer un compte
        </button>
      )}
      {error.kind === "bad_password" && (
        <button
          type="button"
          onClick={onSwitchToForgot}
          className="w-full px-3 py-2 rounded-lg border border-rose-400/60 text-rose-100 text-xs uppercase tracking-widest font-mono-stat hover:bg-rose-400/20"
          data-testid="auth-error-forgot-btn"
        >
          Mot de passe oublié ?
        </button>
      )}
      {error.kind === "email_taken" && (
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="w-full px-3 py-2 rounded-lg bg-rose-400 text-black text-xs uppercase tracking-widest font-mono-stat font-bold hover:bg-rose-300"
          data-testid="auth-error-login-btn"
        >
          Se connecter
        </button>
      )}
    </div>
  );
}

function EmailField({ value, onChange, error, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">
        Email
      </span>
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-black/40 border transition-all ${
          error
            ? "border-rose-400/60"
            : "border-white/10 focus-within:border-cyan-400/60 focus-within:glow-primary"
        }`}
      >
        <Mail className="w-4 h-4 text-cyan-300" />
        <input
          data-testid={testid}
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent outline-none text-white"
          placeholder="ton@email.com"
        />
      </div>
      {error && (
        <span className="block text-[10px] text-rose-300 font-mono-stat mt-1" data-testid={`${testid}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}

function PasswordField({
  value,
  onChange,
  show,
  onToggle,
  testid,
  toggleTestid,
  error,
  label = "Mot de passe",
  placeholder = "••••••••",
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">
        {label}
      </span>
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-black/40 border transition-all ${
          error
            ? "border-rose-400/60"
            : "border-white/10 focus-within:border-cyan-400/60 focus-within:glow-primary"
        }`}
      >
        <Lock className="w-4 h-4 text-cyan-300 shrink-0" />
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          required
          autoComplete={label.includes("Confirmer") ? "new-password" : "current-password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent outline-none text-white"
          placeholder={placeholder}
        />
        <button
          data-testid={toggleTestid}
          type="button"
          onClick={onToggle}
          className="shrink-0 p-1 rounded text-white/50 hover:text-white transition"
          aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          title={show ? "Masquer" : "Afficher"}
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && (
        <span className="block text-[10px] text-rose-300 font-mono-stat mt-1" data-testid={`${testid}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}

function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.12A6.97 6.97 0 0 1 5.48 12c0-.74.13-1.45.36-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
